// Deliberate duplicate of town-square/src/lib/arty-actions/pins-storage.ts (ADR 0002). A change here belongs there too.
import { DEFAULT_PINS, MAX_PINS, effectivePins, normalizePins, togglePin } from './pins';

/**
 * This browser's copy of one member's pins, keyed by member so two artists
 * sharing a browser never inherit each other's shortcuts. `User.pinnedActionIds`
 * is the record; this mirror only answers before the owner query lands. Read
 * through `normalizePins`, so a list edited by hand in devtools cannot put an
 * unknown id on the card.
 *
 * The unkeyed `arty.pins` from before the key carried a member cannot be
 * attributed to anyone, so it is never read and every write removes it.
 */
const LEGACY_KEY = 'arty.pins';

export function pinsKey(asfId: string): string {
  return `${LEGACY_KEY}:${asfId}`;
}

/** `null` when this browser has nothing to say for this member - unknown member, never customised, or junk. */
export function readStoredPins(asfId: string | null | undefined): string[] | null {
  if (!asfId || typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(pinsKey(asfId));
    if (!raw) {
      return null;
    }
    return normalizePins(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** `null` clears it, which is how "never customised" round-trips. A write with no member is dropped. */
export function writeStoredPins(asfId: string | null | undefined, value: string[] | null): void {
  if (!asfId || typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(LEGACY_KEY);
    if (value === null) {
      window.localStorage.removeItem(pinsKey(asfId));
      return;
    }
    window.localStorage.setItem(pinsKey(asfId), JSON.stringify(value));
  } catch {
    // Storage denied: the pins last as long as the tab does.
  }
}

function samePins(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((id, index) => {
    return id === b[index];
  });
}

/**
 * What the card shows: a change the artist made that the server has not echoed
 * yet (`pending`) wins over the server, so a read that lands after the tap
 * cannot put the old list back. Then the server, then this browser's copy -
 * except while the server value is only a cached read the network has not
 * confirmed (`fresh` false): this browser's copy was written by this member's
 * last save, so it beats a cache that may predate it.
 */
export function currentPins(
  pending: string[] | null,
  server: string[] | null,
  local: string[] | null,
  fresh = true
): string[] | null {
  if (pending) {
    return pending;
  }
  if (!fresh) {
    return local ?? server;
  }
  return server ?? local;
}

/**
 * A star tap or a reset, held as the tap itself. `live`: the pins that still
 * pointed at an action when it was made; any other pin is dropped.
 */
export type PinOp = { id: string; live?: readonly string[] } | { reset: true };

/** One member's browser copy, in-flight change, and taps held until their list is known. */
export type PinSlot = { asfId: string | null; local: string[] | null; pending: string[] | null; held?: PinOp[] };

/**
 * The slot for `asfId`, given what this browser holds for them. Taps made while
 * no member was known yet belong to whoever the member turns out to be, so they
 * are kept to replay onto that member's list. Anything held for another member is dropped.
 */
export function slotFor(current: PinSlot, asfId: string | null, stored: string[] | null): PinSlot {
  if (current.asfId === null && current.held?.length && asfId) {
    return { asfId, local: stored, pending: null, held: current.held };
  }
  return { asfId, local: stored, pending: null };
}

/**
 * Whether a tap can be computed against this member's own list: the member is
 * known and either this browser holds their copy or the owner query is theirs.
 * Until then `DEFAULT_PINS` is only a placeholder, so a tap is held, not computed.
 */
export function listKnown(asfId: string | null, local: string[] | null, loaded: boolean): boolean {
  if (!asfId) {
    return false;
  }
  return Boolean(local) || loaded;
}

/**
 * `ops` replayed in order onto `list` (`null` = never customised). A tap that
 * would pin past `MAX_PINS` is dropped and flagged `capped`, as a live tap is
 * refused. No ops, or only dropped ones, returns `list` itself.
 */
export type Replayed = { pins: string[] | null; capped: boolean };

export function applyPinOps(list: string[] | null, ops: readonly PinOp[]): Replayed {
  return ops.reduce<Replayed>(
    (memo, op) => {
      if ('reset' in op) {
        return { ...memo, pins: [...DEFAULT_PINS] };
      }
      const current = effectivePins(memo.pins).filter((pin) => !op.live || op.live.includes(pin));
      if (!current.includes(op.id) && current.length >= MAX_PINS) {
        return { ...memo, capped: true };
      }
      return { ...memo, pins: togglePin(current, op.id) };
    },
    { pins: list, capped: false }
  );
}

/** The pending change, or `null` once the server holds exactly that list. */
export function settledPending(pending: string[] | null, server: string[] | null): string[] | null {
  if (!pending || !server) {
    return pending;
  }
  if (samePins(pending, server)) {
    return null;
  }
  return pending;
}
